import React, { useEffect, useImperativeHandle, useState } from 'react'

import { IQuestionComponentProps, IQuestionComponentRef, QuestionViewFrame } from '../types'
import { IImageResponseValidation, ImageQuestionModelTypes, ImageResponse } from './types'

import useTimeline from '../../modules/timeline/useTimeline'
import useSession from '../../session/useSession'
import QuestionFrame from '../_common/QuestionFrame/QuestionFrame'
import ImageView from './View'

const ImageQuestion = React.memo(
  React.forwardRef<IQuestionComponentRef, IQuestionComponentProps<ImageQuestionModelTypes>>(({ model }, ref) => {
    const [[response, responseValidation, frame], setResponse] = useState<
      [ImageResponse, IImageResponseValidation, QuestionViewFrame]
    >([model.response, model.responseValidation, model.frame])
    const [showValidation, setShowValidation] = useState(false)
    const [frameFullscreenHeight, setFrameFullscreenHeight] = useState(0)

    const session = useSession()
    const timeline = useTimeline()

    useEffect(() => {
      session.enableNext(responseValidation.valid)
    }, [session, responseValidation.valid])

    useImperativeHandle<IQuestionComponentRef, IQuestionComponentRef>(ref, () => {
      return {
        tryNext() {
          if (model.responseValidation.valid || session.skipValidation) {
            return false
          } else {
            session.enableNext(false)
            return true
          }
        },
        tryPrev() {
          return false
        },
        showValidation() {
          setShowValidation(true)
        }
      }
    }, [model, session])

    const onUpdateResponse = (response: ImageResponse) => {
      model.response = response
      setResponse([model.response, model.responseValidation, model.frame])
      setShowValidation(false)
    }

    return (
      <QuestionFrame model={model} noImage={true} onFrameFullscreenHeight={setFrameFullscreenHeight} fullsize={true}>
        <ImageView
          schema={model.schema}
          response={response}
          frameFullscreenHeight={frameFullscreenHeight}
          responseValidation={responseValidation}
          showValidation={showValidation}
          onUpdateResponse={onUpdateResponse}
          frame={frame}
          timeline={timeline}
        />
      </QuestionFrame>
    )
  }),
  (prevProps, nextProps) => prevProps.model === nextProps.model
)

ImageQuestion.displayName = 'ImageQuestion'

export default ImageQuestion
